import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import {
    Wine,
    Coffee,
    Utensils,
    Waves,
    Umbrella,
    Music,
    Building,
    Euro,
    Banknote,
    TrendingUp,
    Calendar,
    ChevronRight,
    Download,
    BarChart3,
    PieChart,
    CalendarDays,
    CalendarRange,
    AlertTriangle,
    X as XIcon
} from 'lucide-react';
import {
    ResponsiveContainer, PieChart as RPieChart, Pie, Cell, Tooltip, Legend,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import api from '../lib/api';
import { DashboardAnalytics } from '../components/DashboardAnalytics';

const BAR_CONFIG = {
    'lobby': { name: 'Lobby Bar', icon: Coffee, color: '#f59e0b' },
    'servis': { name: 'Servis Bar', icon: Utensils, color: '#3b82f6' },
    'vitamin': { name: 'Vitamin Bar', icon: Wine, color: '#22c55e' },
    'havuz': { name: 'Havuz Bar', icon: Waves, color: '#06b6d4' },
    'plaj': { name: 'Plaj Bar', icon: Umbrella, color: '#f97316' },
    'amfi': { name: 'Amfi Bar', icon: Music, color: '#8b5cf6' },
    'teras': { name: 'Teras Bar', icon: Building, color: '#64748b' },
    'alacarte': { name: 'Alacarte Bar', icon: Wine, color: '#e11d48' },
    'minibar': { name: 'Minibar', icon: Coffee, color: '#10b981' },
};

export function Dashboard() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [tcmbRate, setTcmbRate] = useState(null);
    const [chartPeriod, setChartPeriod] = useState('weekly');
    const [monthlyChartData, setMonthlyChartData] = useState([]);
    const [selectedBarFilter, setSelectedBarFilter] = useState(null);
    const analyticsRef = React.useRef(null);
    
    const handleBarSelect = (barId) => {
        setSelectedBarFilter(barId === selectedBarFilter ? null : barId);
        setTimeout(() => {
            analyticsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    useEffect(() => {
        loadDashboardData();
        loadTCMBRate();
        loadMonthlyData();
    }, [selectedDate]);

    // Auto-refresh: TCMB rate every 5 min, dashboard data every 60s
    useEffect(() => {
        const rateInterval = setInterval(() => {
            loadTCMBRate();
        }, 5 * 60 * 1000); // 5 dakika
        const dataInterval = setInterval(() => {
            loadDashboardData();
        }, 60 * 1000); // 1 dakika
        return () => {
            clearInterval(rateInterval);
            clearInterval(dataInterval);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate]);

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            const res = await api.get('/bar-stock-dashboard', { params: { date: selectedDate } });
            setDashboardData(res.data);
        } catch (error) {
            console.error('Dashboard data load error:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadTCMBRate = async () => {
        try {
            const res = await api.get('/tcmb-rate');
            setTcmbRate(res.data);
        } catch (error) {
            console.error('TCMB rate error:', error);
        }
    };

    const loadMonthlyData = async () => {
        try {
            const res = await api.get('/bar-stock-dashboard/monthly', { params: { date: selectedDate } });
            setMonthlyChartData(res.data?.chart_data || []);
        } catch (error) {
            console.error('Monthly data error:', error);
        }
    };

    const handleDownloadReport = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/export/pdf?date=${selectedDate}&guest_count=0`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `rapor_${selectedDate}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('Report download error:', error);
        }
    };

    const formatCurrency = (value, currency = 'TL') => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value) + (currency === 'TL' ? ' ₺' : ' €');
    };

    // Simple bar chart component
    const SimpleBarChart = ({ data }) => {
        const maxValue = Math.max(...data.map(d => d.total_tl), 1);
        
        return (
            <div className="flex items-end gap-2 h-48 mt-4">
                {data.map((item, idx) => (
                    <div key={idx} className="flex-1 flex flex-col items-center">
                        <div className="w-full bg-slate-100 rounded-t relative" style={{ height: '160px' }}>
                            <div 
                                className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-orwion-green to-orwion-green/70 rounded-t transition-all duration-500"
                                style={{ height: `${(item.total_tl / maxValue) * 100}%` }}
                            />
                            {item.total_tl > 0 && (
                                <div className="absolute -top-6 left-0 right-0 text-center text-xs font-medium text-slate-600">
                                    {formatCurrency(item.total_tl)}
                                </div>
                            )}
                        </div>
                        <span className="text-xs text-slate-500 mt-2">{item.label}</span>
                    </div>
                ))}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-32 w-full" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
                </div>
            </div>
        );
    }

    const totals = dashboardData?.totals || { total_tl: 0, total_eur: 0, total_consumption: 0 };
    const bars = dashboardData?.bars || [];
    const chartData = dashboardData?.chart_data || [];

    return (
        <div className="space-y-6" data-testid="dashboard">
            {/* Welcome Banner with Date Selector */}
            <div className="bg-gradient-to-r from-slate-700 to-slate-800 rounded-xl p-4 md:p-6 text-white">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-lg md:text-xl font-bold">
                            Grand Kaptan Bar Stok Programı
                        </h1>
                        <p className="text-white/70 text-sm">
                            Bar stok ve maliyet takibi
                        </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-white/70" />
                            <Input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="bg-white/10 border-white/20 text-white w-auto h-8 text-sm"
                            />
                        </div>
                        {tcmbRate && (
                            <div className="text-xs text-white/60">
                                TCMB Kuru: <span className="text-amber-400 font-semibold">{tcmbRate.rate} ₺</span>
                                <span className="ml-1 opacity-70">({tcmbRate.date})</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <SednaGuestsCard selectedDate={selectedDate} />
            <SednaUnmatchedNotifications />

            {/* Total Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="border-t-4 border-t-green-500">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm font-medium text-slate-500">Toplam Maliyet (TL)</p>
                                <p className="text-3xl font-bold text-slate-800 mt-1">
                                    {formatCurrency(totals.total_tl > 0 ? totals.total_tl : totals.weekly_tl || 0)}
                                </p>
                                <p className="text-xs text-slate-400 mt-1">
                                    {totals.total_tl > 0 ? 'Günlük toplam' : 'Bu hafta (bugün veri yok)'}
                                </p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                                <Banknote className="w-6 h-6 text-green-600" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-100">
                            <div>
                                <div className="text-[10px] text-slate-500">Bugün</div>
                                <div className="text-sm font-bold text-green-700">{formatCurrency(totals.total_tl || 0)}</div>
                            </div>
                            <div>
                                <div className="text-[10px] text-slate-500">Bu Ay</div>
                                <div className="text-sm font-bold text-green-700">{formatCurrency(totals.monthly_tl || 0)}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-t-4 border-t-blue-500">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm font-medium text-slate-500">Toplam Maliyet (EUR)</p>
                                <p className="text-3xl font-bold text-slate-800 mt-1">
                                    {formatCurrency(totals.total_eur > 0 ? totals.total_eur : totals.weekly_eur || 0, 'EUR')}
                                </p>
                                <p className="text-xs text-slate-400 mt-1">Kur: {dashboardData?.euro_rate || tcmbRate?.rate || 35.5} ₺</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                                <Euro className="w-6 h-6 text-blue-600" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-100">
                            <div>
                                <div className="text-[10px] text-slate-500">Bugün</div>
                                <div className="text-sm font-bold text-blue-700">{formatCurrency(totals.total_eur || 0, 'EUR')}</div>
                            </div>
                            <div>
                                <div className="text-[10px] text-slate-500">Bu Ay</div>
                                <div className="text-sm font-bold text-blue-700">{formatCurrency(totals.monthly_eur || 0, 'EUR')}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-t-4 border-t-purple-500">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm font-medium text-slate-500">Toplam Tüketim</p>
                                <p className="text-3xl font-bold text-slate-800 mt-1">
                                    {(Math.round(((totals.total_consumption > 0 ? totals.total_consumption : totals.weekly_consumption) || 0) * 100) / 100).toLocaleString('tr-TR')}
                                </p>
                                <p className="text-xs text-slate-400 mt-1">
                                    {totals.total_consumption > 0 ? 'Ürün adedi' : 'Bu hafta toplam'}
                                </p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                                <TrendingUp className="w-6 h-6 text-purple-600" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-100">
                            <div>
                                <div className="text-[10px] text-slate-500">Bugün</div>
                                <div className="text-sm font-bold text-purple-700">{(Math.round((totals.total_consumption || 0) * 100) / 100).toLocaleString('tr-TR')}</div>
                            </div>
                            <div>
                                <div className="text-[10px] text-slate-500">Bu Ay</div>
                                <div className="text-sm font-bold text-purple-700">{(Math.round((totals.monthly_consumption || 0) * 100) / 100).toLocaleString('tr-TR')}</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Bar Cards Grid */}
            <div>
                <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-orwion-green" />
                    Bar Bazlı Maliyet Dağılımı
                </h2>

                {/* Bar Cost Comparison Charts (Pie + Bar) */}
                {bars && bars.length > 0 && totals.total_tl > 0 && (
                    <BarCostBreakdownCharts bars={bars} totalTl={totals.total_tl} />
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {bars.map((bar) => {
                        const config = BAR_CONFIG[bar.bar_id] || { icon: Wine, color: '#64748b' };
                        const BarIcon = config.icon;
                        const percentage = totals.total_tl > 0 
                            ? ((bar.total_tl / totals.total_tl) * 100).toFixed(1) 
                            : 0;

                        return (
                            <button
                                key={bar.bar_id}
                                type="button"
                                onClick={() => handleBarSelect(bar.bar_id)}
                                className="block text-left w-full"
                                data-testid={`bar-card-${bar.bar_id}`}
                            >
                                <Card className={`hover:shadow-lg transition-all cursor-pointer border-l-4 ${selectedBarFilter === bar.bar_id ? 'ring-2 ring-purple-400 shadow-xl' : ''}`}
                                      style={{ borderLeftColor: config.color }}>
                                    <CardContent className="p-4">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <div 
                                                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                                                    style={{ backgroundColor: `${config.color}15` }}
                                                >
                                                    <BarIcon className="w-5 h-5" style={{ color: config.color }} />
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-slate-800">{bar.bar_name}</p>
                                                    <Badge 
                                                        variant="outline" 
                                                        className={bar.has_data ? 'text-green-600' : 'text-slate-400'}
                                                    >
                                                        {bar.has_data ? 'Veri var' : 'Veri yok'}
                                                    </Badge>
                                                </div>
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-slate-300" />
                                        </div>
                                        
                                        <div className="space-y-2">
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm text-slate-500">TL:</span>
                                                <span className="font-semibold text-slate-800">
                                                    {formatCurrency(bar.total_tl)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm text-slate-500">EUR:</span>
                                                <span className="font-medium text-slate-600">
                                                    {formatCurrency(bar.total_eur, 'EUR')}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm text-slate-500">Tüketim:</span>
                                                <span className="font-medium text-slate-600">
                                                    {(Math.round((bar.consumption || 0) * 100) / 100).toLocaleString('tr-TR')} adet
                                                </span>
                                            </div>
                                            
                                            {/* Weekly + Monthly cumulative */}
                                            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
                                                <div className="text-center bg-blue-50 rounded p-1.5">
                                                    <div className="text-[10px] text-blue-600 font-medium">Bu Hafta (7g)</div>
                                                    <div className="text-xs font-bold text-blue-800">{formatCurrency(bar.weekly_tl || 0)}</div>
                                                </div>
                                                <div className="text-center bg-purple-50 rounded p-1.5">
                                                    <div className="text-[10px] text-purple-600 font-medium">Bu Ay (30g)</div>
                                                    <div className="text-xs font-bold text-purple-800">{formatCurrency(bar.monthly_tl || 0)}</div>
                                                </div>
                                            </div>
                                            
                                            {/* Progress bar for percentage */}
                                            <div className="mt-3">
                                                <div className="flex justify-between text-xs text-slate-500 mb-1">
                                                    <span>Toplam içindeki pay</span>
                                                    <span>%{percentage}</span>
                                                </div>
                                                <div className="w-full bg-slate-100 rounded-full h-2">
                                                    <div 
                                                        className="h-2 rounded-full transition-all duration-500"
                                                        style={{ 
                                                            width: `${percentage}%`,
                                                            backgroundColor: config.color
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Weekly/Monthly Chart */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="font-heading text-lg flex items-center gap-2">
                                <BarChart3 className="w-5 h-5 text-orwion-green" />
                                Maliyet Grafiği
                            </CardTitle>
                            <CardDescription>
                                {chartPeriod === 'weekly' ? 'Son 7 günlük' : 'Son 30 günlük'} toplam maliyet (TL)
                            </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="flex bg-slate-100 rounded-lg p-0.5">
                                <Button
                                    variant={chartPeriod === 'weekly' ? 'default' : 'ghost'}
                                    size="sm"
                                    className={chartPeriod === 'weekly' ? 'h-7 text-xs' : 'h-7 text-xs text-slate-500'}
                                    onClick={() => setChartPeriod('weekly')}
                                    data-testid="chart-weekly-btn"
                                >
                                    <CalendarDays className="w-3.5 h-3.5 mr-1" />
                                    Haftalık
                                </Button>
                                <Button
                                    variant={chartPeriod === 'monthly' ? 'default' : 'ghost'}
                                    size="sm"
                                    className={chartPeriod === 'monthly' ? 'h-7 text-xs' : 'h-7 text-xs text-slate-500'}
                                    onClick={() => setChartPeriod('monthly')}
                                    data-testid="chart-monthly-btn"
                                >
                                    <CalendarRange className="w-3.5 h-3.5 mr-1" />
                                    Aylık
                                </Button>
                            </div>
                            <Button variant="outline" size="sm" onClick={handleDownloadReport} data-testid="download-report-btn">
                                <Download className="w-4 h-4 mr-2" />
                                Rapor İndir
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <SimpleBarChart data={chartPeriod === 'weekly' ? chartData : monthlyChartData} />
                </CardContent>
            </Card>

            {/* Analytics Widgets (Kategori karşılaştırma + Top ürünler + Otomatik talep) */}
            <div ref={analyticsRef} className="space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <h2 className="font-heading text-xl font-bold text-slate-800 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-orwion-green" />
                        Analiz & Tahmin
                        {selectedBarFilter && (
                            <Badge className="bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200">
                                {BAR_CONFIG[selectedBarFilter]?.name || selectedBarFilter}
                            </Badge>
                        )}
                    </h2>
                    {selectedBarFilter && (
                        <Button variant="ghost" size="sm" onClick={() => setSelectedBarFilter(null)} data-testid="clear-bar-filter-btn">
                            Tüm Barlar
                        </Button>
                    )}
                </div>
                <DashboardAnalytics barId={selectedBarFilter} />
            </div>

        </div>
    );
}

// ===== Sedna "Bugün Otelde" Kartı (tarih bazlı) =====
function SednaGuestsCard({ selectedDate }) {
    const [data, setData] = useState(null);
    const [loaded, setLoaded] = useState(false);
    useEffect(() => {
        let cancelled = false;
        setLoaded(false);
        (async () => {
            try {
                const r = await api.get(`/sedna/guests/${selectedDate}`);
                if (cancelled) return;
                setData(r.data && r.data.available ? r.data : null);
            } catch { if (!cancelled) setData(null); }
            finally { if (!cancelled) setLoaded(true); }
        })();
        return () => { cancelled = true; };
    }, [selectedDate]);
    if (!loaded) return null;
    if (!data || !data.guest_counts) {
        return (
            <Card className="border-l-4 border-l-slate-300 bg-slate-50" data-testid="sedna-dashboard-card-empty">
                <CardContent className="py-2.5 px-5 text-sm text-slate-500">
                    <span className="font-semibold text-slate-600">Sedna · {selectedDate}</span> — bu tarihte misafir kaydı yok
                </CardContent>
            </Card>
        );
    }
    const g = data.guest_counts;
    const sameDay = data.report_date === selectedDate;
    return (
        <Card className="border-l-4 border-l-indigo-500 bg-gradient-to-r from-indigo-50/60 to-white" data-testid="sedna-dashboard-card">
            <CardContent className="py-3 px-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <div className="text-[11px] uppercase tracking-wide text-indigo-700 font-bold">{sameDay ? 'Bugün Otelde' : 'Otelde'}</div>
                        <div className="text-[10px] text-slate-500">Sedna · {data.report_date}</div>
                    </div>
                    <div className="flex items-center gap-5 lg:gap-7">
                        <div className="text-center"><div className="text-[10px] text-slate-500 uppercase">Yetişkin</div><div className="text-xl font-bold text-slate-800">{g.adults ?? 0}</div></div>
                        <div className="text-center"><div className="text-[10px] text-slate-500 uppercase">Ücretli Çocuk</div><div className="text-xl font-bold text-slate-800">{g.paid_children ?? 0}</div></div>
                        <div className="text-center border-l border-slate-300 pl-5"><div className="text-[10px] text-slate-500 uppercase">Toplam Misafir</div><div className="text-2xl font-bold text-indigo-700">{g.total_guests ?? 0}</div></div>
                        <div className="text-center hidden sm:block"><div className="text-[10px] text-slate-500 uppercase">Dolu Oda</div><div className="text-xl font-bold text-slate-800">{g.occupied_rooms ?? 0}</div></div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

// ===== Bar Cost Breakdown Charts (Recharts) =====
function BarCostBreakdownCharts({ bars, totalTl }) {
    // bars: [{bar_id, bar_name, total_tl, total_eur, consumption, has_data}]
    const data = (bars || [])
        .filter(b => (b.total_tl || 0) > 0)
        .map(b => ({
            name: b.bar_name,
            bar_id: b.bar_id,
            tl: Math.round(b.total_tl),
            eur: Math.round(b.total_eur),
            consumption: b.consumption,
            pct: totalTl > 0 ? +((b.total_tl / totalTl) * 100).toFixed(1) : 0,
        }))
        .sort((a, b) => b.tl - a.tl);
    if (data.length === 0) return null;

    const colorPalette = ['#0d9488', '#0ea5e9', '#8b5cf6', '#f59e0b', '#ec4899', '#10b981', '#6366f1', '#ef4444', '#14b8a6'];

    return (
        <Card className="mb-4 bg-gradient-to-br from-slate-50 to-white" data-testid="bar-cost-breakdown-charts">
            <CardContent className="py-4">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                    {/* Pie — Cost distribution */}
                    <div className="lg:col-span-2 h-64">
                        <h3 className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Toplam Maliyet Dağılımı</h3>
                        <ResponsiveContainer width="100%" height="90%">
                            <RPieChart>
                                <Pie
                                    data={data}
                                    dataKey="tl"
                                    nameKey="name"
                                    cx="50%" cy="50%"
                                    innerRadius={42} outerRadius={88}
                                    paddingAngle={2}
                                    label={({ name, pct }) => `${pct}%`}
                                    labelLine={false}
                                >
                                    {data.map((_, i) => (
                                        <Cell key={i} fill={colorPalette[i % colorPalette.length]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{ background: '#0f172a', border: 'none', borderRadius: 8, color: '#f1f5f9', fontSize: 12 }}
                                    formatter={(v, _name, payload) => [`${Number(v).toLocaleString('tr-TR')} TL · %${payload.payload.pct}`, payload.payload.name]}
                                />
                            </RPieChart>
                        </ResponsiveContainer>
                    </div>
                    {/* Horizontal Bar — Ranking */}
                    <div className="lg:col-span-3 h-64">
                        <h3 className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Bar Bazında Sıralama (TL)</h3>
                        <ResponsiveContainer width="100%" height="90%">
                            <BarChart data={data} layout="vertical" margin={{ left: 0, right: 16, top: 4, bottom: 4 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false}/>
                                <XAxis type="number" tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v} tick={{ fontSize: 11, fill: '#64748b' }}/>
                                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#475569' }} width={90}/>
                                <Tooltip
                                    contentStyle={{ background: '#0f172a', border: 'none', borderRadius: 8, color: '#f1f5f9', fontSize: 12 }}
                                    formatter={(v) => [`${Number(v).toLocaleString('tr-TR')} TL`, 'Maliyet']}
                                />
                                <Bar dataKey="tl" radius={[0, 6, 6, 0]}>
                                    {data.map((_, i) => (
                                        <Cell key={i} fill={colorPalette[i % colorPalette.length]}/>
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

// ===== Sedna Unmatched Notifications (Dashboard) =====
function SednaUnmatchedNotifications() {
    const [items, setItems] = useState([]);
    const [collapsed, setCollapsed] = useState(false);

    const load = async () => {
        try {
            const r = await api.get('/sedna/unmatched?days=30');
            setItems(r.data?.items || []);
        } catch { setItems([]); }
    };
    useEffect(() => { load(); }, []);

    const dismiss = async (id) => {
        try {
            await api.post(`/sedna/unmatched/${id}/dismiss`);
            setItems(prev => prev.filter(u => u.id !== id));
        } catch {/* silent */}
    };
    const dismissAll = async () => {
        try {
            await api.post('/sedna/unmatched/dismiss-all');
            setItems([]);
        } catch {/* silent */}
    };

    if (!items.length) return null;

    // Group by bar_id and date for cleaner display
    const grouped = items.reduce((acc, u) => {
        const key = `${u.bar_id}|${u.report_date}`;
        if (!acc[key]) acc[key] = { bar_id: u.bar_id, report_date: u.report_date, items: [] };
        acc[key].items.push(u);
        return acc;
    }, {});

    return (
        <Card className="border-l-4 border-l-amber-500 bg-amber-50/60" data-testid="dashboard-sedna-unmatched">
            <CardContent className="py-3 px-5">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-amber-900">
                                Sedna&apos;dan çekilen ama tanımlı olmayan {items.length} ürün (son 30 gün)
                            </div>
                            <div className="text-xs text-amber-700">İlgili bara gidip ürünleri elle ekleyin, sonra &quot;Eklendi&quot; diyerek listeden çıkarın.</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" className="text-amber-700 hover:bg-amber-100 h-7" onClick={() => setCollapsed(c => !c)} data-testid="dashboard-sedna-unmatched-toggle">
                            {collapsed ? 'Göster' : 'Gizle'}
                        </Button>
                        {items.length > 1 && (
                            <Button size="sm" variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-100 h-7" onClick={dismissAll} data-testid="dashboard-sedna-unmatched-dismiss-all">
                                Hepsini Kapat
                            </Button>
                        )}
                    </div>
                </div>
                {!collapsed && (
                    <div className="mt-3 space-y-3 max-h-80 overflow-y-auto" data-testid="dashboard-sedna-unmatched-list">
                        {Object.values(grouped).map(grp => (
                            <div key={`${grp.bar_id}|${grp.report_date}`} className="bg-white border border-amber-200 rounded p-2">
                                <div className="flex items-center justify-between mb-1.5">
                                    <Link to={`/bar/${grp.bar_id}`} className="text-xs font-bold uppercase tracking-wide text-amber-900 hover:underline">
                                        {grp.bar_id.toUpperCase()} · {grp.report_date}
                                    </Link>
                                    <span className="text-[10px] text-slate-500">{grp.items.length} ürün</span>
                                </div>
                                <ul className="space-y-1">
                                    {grp.items.map(u => (
                                        <li key={u.id} className="flex items-center justify-between gap-2 text-sm border-t border-amber-100 pt-1.5" data-testid={`dashboard-sedna-unmatched-item-${u.id}`}>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-slate-800 truncate">
                                                    <span className="font-semibold">{u.product_name}</span>
                                                    {u.product_code && <span className="ml-1.5 text-[11px] font-mono text-slate-500">#{u.product_code}</span>}
                                                </div>
                                                <div className="text-xs text-slate-600">
                                                    {Number(u.quantity).toLocaleString('tr-TR')} {u.unit || ''} · {u.section === 'sarf_malzeme' ? 'Sarf' : 'Bar Stok'}
                                                </div>
                                            </div>
                                            <button onClick={() => dismiss(u.id)} className="p-1 rounded hover:bg-amber-100" title="Eklendi olarak işaretle" data-testid={`dashboard-sedna-unmatched-dismiss-${u.id}`}>
                                                <XIcon className="w-3.5 h-3.5 text-amber-700" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default Dashboard;
